import type { EditorAPI } from '#packages/editorAPI';
import { translate } from '#packages/i18n';
import * as util from '#packages/util';
import {
  type EditorIntegrationAPI,
  TranslationContext,
  WidgetPluginsManager,
  type WidgetPointer,
  type WidgetPlugin,
} from '@wix/editor-platform-host-integration/ui';
import React, { useState } from 'react';
import type { CompRef } from 'types/documentServices';
import type {
  MapDispatchToProps,
  MapStateToProps,
  ThunkAction,
} from 'types/redux';
import { TpaSettingsPanelFrame } from '../frames';
import { HttpClient } from '@wix/http-client';
import type { PluginPlacement } from '@wix/ambassador-app-service-webapp/types';
import { type PluginActionContext } from '@wix/editor-platform-host-integration-apis';

interface PluginsPanelStateProps {
  widgetRef: CompRef;
  widgetName: string;
}

interface PluginsPanelDispatchProps {
  openHelpArticle: () => void;
  getEditorIntegrationAPI: () => EditorIntegrationAPI;
}

interface PluginsPanelOwnProps {
  panelName: string;
  style?: Record<string, string>;
  selectedComponents: CompRef[];
  onClose?: () => void;
  isBlocks?: boolean;
  onInstallationComplete?: (widgetPlugin: WidgetPointer) => void;
}

interface PluginsPanelProps
  extends PluginsPanelStateProps,
    PluginsPanelDispatchProps,
    PluginsPanelOwnProps {}

const PluginSettingsMaxHeight = 490; // https://jira.wixpress.com/browse/EP-2484

const PluginsPanel: React.FC<PluginsPanelProps> = (props) => {
  const [editorIntegrationAPI] = useState(() =>
    props.getEditorIntegrationAPI(),
  );

  return (
    <TpaSettingsPanelFrame
      panelName={props.panelName}
      title={translate('PLATFORM_Widget_Slots_Modal_Header')}
      width={790}
      height={PluginSettingsMaxHeight}
      shouldAddScroll
      style={props.style}
      onClose={props.onClose}
      onHelpClicked={props.openHelpArticle}
    >
      <TranslationContext.Provider value={translate}>
        <WidgetPluginsManager
          editorIntegrationAPI={editorIntegrationAPI}
          widgetRef={props.widgetRef}
          widgetName={props.widgetName}
        />
      </TranslationContext.Provider>
    </TpaSettingsPanelFrame>
  );
};

const getEditorAPI: ThunkAction = (_dispatch, _getState, { editorAPI }) =>
  editorAPI;

const getSelectedComponents = (editorAPI: EditorAPI) =>
  editorAPI.selection.getSelectedComponents();

const mapStateToProps: MapStateToProps<
  PluginsPanelStateProps,
  PluginsPanelOwnProps
> = ({ editorAPI }) => {
  const widgetRef =
    editorAPI.platform.widgetPlugins.getWidgetHostRefByWidgetRef(
      getSelectedComponents(editorAPI)[0],
    );

  const nearestWidgetHostRef =
    editorAPI.platform.widgetPlugins.getNearestWidgetHostRef(widgetRef);
  const widgetName = editorAPI.components.getDisplayName(nearestWidgetHostRef);

  return {
    widgetRef,
    widgetName,
  };
};

const mapDispatchToProps: MapDispatchToProps<
  PluginsPanelDispatchProps,
  PluginsPanelOwnProps
> = (dispatch, { panelName, isBlocks, onInstallationComplete }) => {
  const editorAPI = dispatch(getEditorAPI);

  return {
    openHelpArticle: () => {
      const widgetPluginsHelpArticleId = '19eb165a-b55d-4e7e-8bd9-067c9cbb9cb5';
      editorAPI.panelManager.openHelpCenter(widgetPluginsHelpArticleId);
    },
    getEditorIntegrationAPI: () =>
      getEditorIntegrationAPI(editorAPI, {
        panelName,
        isBlocks,
        onInstallationComplete,
        selectedComponents: getSelectedComponents(editorAPI),
      }),
  };
};

function getEditorIntegrationAPI(
  editorAPI: EditorAPI,
  options: {
    panelName: string;
    isBlocks?: boolean;
    onInstallationComplete?: (widgetPlugin: WidgetPointer) => void;
    selectedComponents: CompRef[];
  },
): EditorIntegrationAPI {
  const META_SITE_APPLICATION_ID = '-666';
  const getAppToken = () =>
    editorAPI.platform.getAppDataByApplicationId(META_SITE_APPLICATION_ID)
      ?.instance;
  const signedPanelHttpClient = new HttpClient({
    baseURL: '/_serverless/editor-platform-widget-plugins/',
    getAppToken,
  });
  return {
    getWidgetPluginsRequest: (params: {
      includeApps?: string[];
      placements?: PluginPlacement[];
      slotIds?: string[];
      excludePluginTypes: string[];
    }) => {
      return getWidgetPluginsRequest({
        ...params,
        httpClient: signedPanelHttpClient,
      });
    },
    isBlocks: () => options.isBlocks ?? false,
    getWidgetSlots: (widgetCompRef: CompRef) => {
      // when opened a default plugins modal setting in blocks
      if (options.isBlocks) {
        if (!window.appStudioModel.devSiteAppDefId) {
          // if blocks app was never saved yet it means that
          // no appDefId was created and we have no active placement
          // therefore no available slots to install plugins
          // TODO ask if we could improve this
          return [];
        }
        const widgetSlot = editorAPI.platform.widgetPlugins.getSlotInBlocks(
          options.selectedComponents[0],
        );

        return widgetSlot ? [widgetSlot] : [];
      }
      // when selected a populated slot
      const scopedSlotPlaceholder =
        editorAPI.platform.widgetPlugins.getSlotPlaceholderRefByContentRef(
          widgetCompRef,
        );
      if (scopedSlotPlaceholder) {
        return [
          editorAPI.platform.widgetPlugins.getWidgetSlot(
            scopedSlotPlaceholder,
            { includePrivate: false },
          ),
        ];
      }
      // when selected a host widget
      return editorAPI.platform.widgetPlugins.getWidgetSlots(widgetCompRef, {
        includePrivate: false,
      });
    },
    getSlotsData: editorAPI.dsRead.components.slots.getSlotsData,
    getWidgetSlotData: editorAPI.components.data.get,
    getNearestWidgetHostRef:
      editorAPI.platform.widgetPlugins.getNearestWidgetHostRef,
    installWidgetPlugin: async (
      slotPlaceholderCompRef: CompRef,
      widgetPointer: WidgetPointer,
      pluginActionContext: PluginActionContext,
    ) => {
      const compRef =
        await editorAPI.platform.widgetPlugins.installWidgetPlugin(
          slotPlaceholderCompRef,
          widgetPointer,
          pluginActionContext,
        );

      await editorAPI.waitForChangesAppliedAsync();
      editorAPI.selection.selectComponentByCompRef(compRef);
      options.onInstallationComplete?.(widgetPointer);
    },
    uninstallWidgetPlugin: (
      slotComponentRef: CompRef,
      pluginActionContext: PluginActionContext,
    ) => {
      return editorAPI.platform.widgetPlugins.uninstallWidgetPlugin(
        slotComponentRef,
        pluginActionContext,
      );
    },
    openPanel: editorAPI.panelManager.openPanel,
    closePanel: (panelName: string) => {
      editorAPI.panelManager.closePanelByName(panelName);
    },
    highlightSlot: editorAPI.platform.widgetPlugins.highlightSlot,
    showOnPage: editorAPI.platform.widgetPlugins.showOnPage,
    unhighlightSlot: editorAPI.platform.widgetPlugins.unhighlightSlot,
    closeWidgetPluginsPanel: () => {
      editorAPI.panelManager.closePanelByName(options.panelName);
    },
    isWidgetPluginMoveActionEnabled: (slotPlaceholderCompRef: CompRef) =>
      editorAPI.platform.widgetPlugins.isSlotContentPotentialMovable(
        slotPlaceholderCompRef,
      ),
    movePlugin: (compRef: CompRef) => {
      editorAPI.panelManager.openPanel(
        'panels.widgetPanels.slotSelectionExtendedPanel',
        {
          slotsPlaceholderCompRef: compRef,
        },
      );

      editorAPI.platform.widgetPlugins.scrollToSlot(compRef);
      editorAPI.selection.selectComponentByCompRef(compRef);
    },
    openReplacePluginHelpArticle: () => {
      const helpArticleId = '68ab8d2f-c2d2-4601-95d0-52d25ba1c24d';
      const languageCode = util.languages.getLanguageCode();
      const url = new URL(
        `https://wix.wixanswers.com/apps/widget/v1/wix/53f85d67-f8b0-41ca-9dc8-5a16390cc74f/view/${languageCode}/article/${helpArticleId}`,
      );

      window.open(url, '_blank');
    },
  };
}

export default util.hoc.connect(
  util.hoc.STORES.EDITOR_API,
  mapStateToProps,
  mapDispatchToProps,
)(PluginsPanel);

interface GetWidgetPluginsProps {
  includeApps?: string[];
  placements?: PluginPlacement[];
  slotIds?: string[];
  httpClient: HttpClient;
  excludePluginTypes?: string[];
}

async function getWidgetPluginsRequest(
  params: GetWidgetPluginsProps,
): Promise<WidgetPlugin[]> {
  if (params.placements?.length === 0) {
    return [];
  }

  const queryParams = new URLSearchParams();

  if (params.includeApps?.length) {
    queryParams.append('includeApps', params.includeApps.join(','));
  }

  if (params.placements?.length) {
    queryParams.append('placements', JSON.stringify(params.placements));
  }

  if (params.slotIds?.length) {
    queryParams.append('slotIds', params.slotIds.join(','));
  }

  if (params.excludePluginTypes?.length) {
    queryParams.append(
      'excludePluginTypes',
      params.excludePluginTypes.join(','),
    );
  }

  const res = await params.httpClient.get<WidgetPlugin[]>(
    `/plugins?${queryParams.toString()}`,
  );

  return res.data;
}
